import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import './GiftCard.scss';
import { useTranslation } from 'react-i18next';
import { Button } from 'react-bootstrap';

import { addError, addWarning, dismissType } from 'actions/Error';
import { getGiftCards } from 'services/ErplyAPI/payment';
import {
  getCurrencyFormatter,
  getPosDefaultLanguage,
} from 'reducers/configs/settings';
import InputField from 'components/FieldTypes/InputField';
import { getRegularGiftcards, getGiftcardTypes } from 'reducers/giftcards';
import { setPayment, setPaymentSelected } from 'actions/Payments';
import {
  getBalance,
  getIsGiftcardDisabled,
  getIsSerialGiftcardDisabled,
  getNewPaymentKeyForType,
  getPayments,
  getPaymentSelected,
  getPaymentsCurrency,
} from 'reducers/Payments';
import { PluginComponent } from 'plugins';

const GiftCard = () => {
  const { t } = useTranslation('payment');
  const regularGiftCards = useSelector(getRegularGiftcards);

  const { rate: currencyRate } = useSelector(getPaymentsCurrency);

  const [state, setState] = useState({
    serialCardNumber: '',
    regularCardAmount: '',
    regularGiftCards,
    selectedCard: '0',
    regularCardsLoaded: true,
    giftCardNotes: '',
  });
  const lang = useSelector(getPosDefaultLanguage);
  const langIndicator = lang.isoCode?.toUpperCase();

  const dispatch = useDispatch();

  const payments = useSelector(getPayments);
  const paymentKey = useSelector(getNewPaymentKeyForType('giftCard'));
  const balance = useSelector(getBalance);
  const isGiftcardDisabled = useSelector(getIsGiftcardDisabled);
  const isSerialGiftcardDisabled = useSelector(getIsSerialGiftcardDisabled);
  const gcTypes = useSelector(getGiftcardTypes);
  const typeID = useSelector(getPaymentSelected).match(/giftcard-(.+)/)?.[1];
  const giftCard = gcTypes.find(t => String(t.id) === String(typeID));
  // check giftCard for the name in POS lang, if no name, fallback to ENG
  const type = giftCard?.names[langIndicator] || giftCard?.names.EN;

  // Serial field shown if either:
  //    1) Opened from button of specific type
  // or 2) There are no specific type buttons
  const showSerialField =
    !isSerialGiftcardDisabled && (type || gcTypes.length === 0);

  // Regular field shown if:
  //    1) Opened from regular button (not a specific type button)
  const showRegularField = !isGiftcardDisabled && !type;

  const searchSerialGiftCard = async () => {
    dispatch(dismissType('giftCard'));
    if (isSerialGiftcardDisabled) {
      dispatch(
        addWarning(t('alerts.serialGiftcardDisabled'), {
          dismissible: false,
          errorType: 'giftCard',
          selfDismiss: 2000,
        }),
      );
      return false;
    }
    // check for empty serial field
    const code = state.serialCardNumber?.trim();
    if (!code) {
      dispatch(
        addWarning(t('alerts.giftcardSerialRequired'), {
          dismissible: false,
          errorType: 'giftCard',
          selfDismiss: 2000,
        }),
      );
      return false;
    }

    // check for card being used in the payment already
    const existsInPayments = Object.values(payments).filter(
      payment => payment.serial === code,
    ).length;
    if (existsInPayments) {
      dispatch(
        addWarning(t('alerts.giftcardSerialAlreadyAdded', { serial: code }), {
          dismissible: false,
          errorType: 'giftCard',
          selfDismiss: 2000,
        }),
      );
      return false;
    }

    // fetch the requested card
    dispatch(
      addWarning(t('alerts.fetchingSerialGiftcard', { serial: code }), {
        dismissible: false,
        errorType: 'giftCard',
        selfDismiss: 30000,
      }),
    );
    try {
      // Check for card existence
      const [card] = await getGiftCards({ code });
      dispatch(dismissType('giftCard'));
      if (!card) {
        dispatch(
          addError(t('alerts.giftcardSerialNotFound', { serial: code }), {
            dismissible: true,
            errorType: 'giftCard',
            selfDismiss: 3000,
          }),
        );
        return false;
      }

      // check the expiry date
      const expiryDate = new Date(card.expirationDate);
      const today = new Date();
      if (expiryDate.setDate(expiryDate.getDate() + 1) < today.getTime()) {
        dispatch(
          addError(t('giftcardExpired'), {
            dismissible: true,
            errorType: 'giftCard',
            selfDismiss: 3000,
          }),
        );
        return false;
      }

      // check if it's used up
      if (card.balance <= 0) {
        dispatch(
          addError(
            t('alerts.giftcardInsufficientBalance', {
              serial: code,
              balance: card.balance,
            }),
            {
              dismissible: true,
              errorType: 'giftCard',
              selfDismiss: 3000,
            },
          ),
        );
        return false;
      }

      let paymentAmount = card.balance;
      if (paymentAmount + balance > 0) paymentAmount -= paymentAmount + balance;

      const payment = {
        amount: paymentAmount,
        key: paymentKey,
        caption:
          gcTypes.find(type => String(type.id) === String(card.typeID))
            ?.nameEN ?? t('tenders.giftcard'),
        type: 'GIFTCARD',
        serial: code,
        giftCardID: card.giftCardID,
        giftCardTypeID: card.typeID,
        giftCardBalance: card.balance,
        vatrateID: card.vatrateID,
        currencyRate,
      };
      if (typeID && String(typeID) !== String(card.typeID)) {
        dispatch(
          addWarning(
            t('alerts.giftcardWrongType', {
              fromType: type,
              toType: gcTypes.find(t => t.typeID === card.typD)?.name,
            }),
          ),
        );
      }
      await dispatch(setPayment(payment));
      return dispatch(setPaymentSelected(''));
    } catch (e) {
      return false;
    }
  };

  const sanitizeAmount = raw => {
    let clean = raw
      .replace(/[,]/g, '.')
      .replace(/[^0-9.]/g, '')
      .replace(/[.](?=.*[.])/g, '');
    if (clean.match(/^[.]/g)) clean = clean.replace(/^[.]/g, '0.');
    if (clean.match(/^[0]+[1-9]/g)) clean = clean.replace(/^[0]/g, '');
    return clean;
  };

  const handleChange = e => {
    let stateValue = e.target.value;
    switch (e.target.name) {
      case 'regularCardSelect':
        setState({ ...state, selectedCard: stateValue });
        break;
      case 'regularCardAmount':
        stateValue = sanitizeAmount(stateValue);
        setState({ ...state, [e.target.name]: stateValue });
        break;
      default:
        setState({ ...state, [e.target.name]: stateValue });
    }
  };

  const setRegularGiftCard = () => {
    let paymentAmount = Number(state.regularCardAmount);
    dispatch(dismissType('giftCard'));
    if (isGiftcardDisabled) {
      dispatch(
        addWarning(t('alerts.regularGiftcardDisabled'), {
          dismissible: false,
          errorType: 'giftCard',
          selfDismiss: 2000,
        }),
      );
      return false;
    }
    if (!paymentAmount) {
      dispatch(
        addWarning(t('alerts.giftcardAmountRequired'), {
          dismissible: false,
          errorType: 'giftCard',
          selfDismiss: 2000,
        }),
      );
      return false;
    }
    if (!state.regularGiftCards[state.selectedCard]) {
      dispatch(
        addWarning(t('alerts.giftCardRequired'), {
          dismissible: false,
          errorType: 'giftCard',
          selfDismiss: 2000,
        }),
      );
      return false;
    }
    if (paymentAmount + balance > 0) {
      paymentAmount = Math.abs(Math.min(paymentAmount, balance));
    }
    const payment = {
      amount: paymentAmount,
      key: paymentKey,
      caption: t('tenders.giftcard'),
      type: 'GIFTCARD',
      vatrateID: state.regularGiftCards[state.selectedCard].vatRateID,
      cardHolder: state.giftCardNotes || null,
    };
    return dispatch(setPayment(payment)).then(() =>
      dispatch(setPaymentSelected('')),
    );
  };

  // If at the moment of gift card menu opening redux still does not have gift cards, but gets populated with them later, populate the state of the component
  useEffect(() => {
    if (!state.regularGiftCards && regularGiftCards) {
      setState(prevState => ({
        ...prevState,
        regularGiftCards,
      }));
    }
  }, [regularGiftCards, state.regularGiftCards]);

  const goBack = () => {
    dispatch(setPaymentSelected(''));
  };

  const isDisabled =
    isGiftcardDisabled ||
    !state.regularGiftCards?.[state.selectedCard] ||
    !Number(state.regularCardAmount);

  const formatCurrency = useSelector(getCurrencyFormatter);
  return (
    <PluginComponent hookname="UIGiftCard" props={{ typeID }}>
      <Form data-testid="giftcard-form" className="form-giftCard">
        <Form.Group style={{ display: showSerialField ? undefined : 'none' }}>
          <span>
            {t('serialGiftcard.title', {
              context: type ? 'type' : undefined,
              type,
            })}
          </span>
          <InputGroup size="lg">
            <Form.Control
              disabled={isSerialGiftcardDisabled}
              value={state.serialCardNumber}
              data-testid="serial-number"
              name="serialCardNumber"
              placeholder={
                type ??
                t('serialGiftcard.fields.code', {
                  context: 'placeholder',
                })
              }
              onChange={handleChange}
            />

            <InputGroup.Append data-testid="search-giftcard">
              <InputGroup.Text
                className={isSerialGiftcardDisabled ? 'disabled' : undefined}
                onClick={searchSerialGiftCard}
              >
                <span className="icon_search flip-horizontal" />
              </InputGroup.Text>
            </InputGroup.Append>

            <InputGroup.Append data-testid="go-back" disabled>
              <InputGroup.Text disabled onClick={goBack}>
                <span className="icon_close" />
              </InputGroup.Text>
            </InputGroup.Append>
          </InputGroup>
        </Form.Group>
        <Form.Group style={{ display: showRegularField ? undefined : 'none' }}>
          <div className="d-flex justify-content-between">
            <span>{t('regularGiftcard.title')}</span>
            <span data-testid="balance">
              {t('regularGiftcard.balance', {
                balance: formatCurrency(balance),
              })}
            </span>
          </div>

          <div className="cards-regular">
            <div>
              <InputField
                disabled={isGiftcardDisabled}
                data-testid="select"
                size="lg"
                name="regularCardSelect"
                type="select"
                options={state.regularGiftCards}
                onChange={handleChange}
                value={state.selectedCard}
              />
            </div>
            <div>
              <InputGroup size="lg">
                <Form.Control
                  disabled={isGiftcardDisabled}
                  value={state.regularCardAmount}
                  data-testid="amount"
                  name="regularCardAmount"
                  placeholder={t('regularGiftcard.fields.amount', {
                    context: 'placeholder',
                  })}
                  onChange={handleChange}
                />
                <InputGroup.Append>
                  <Button
                    disabled={isDisabled}
                    variant="primary"
                    className={isDisabled ? 'disabled' : undefined}
                    onClick={setRegularGiftCard}
                    data-testid="check"
                  >
                    <span className="icon_check" />
                  </Button>
                </InputGroup.Append>
                <InputGroup.Append data-testid="close">
                  <InputGroup.Text onClick={goBack}>
                    <span className="icon_close" />
                  </InputGroup.Text>
                </InputGroup.Append>
              </InputGroup>
            </div>
          </div>

          <InputGroup size="lg">
            <Form.Control
              as="textarea"
              data-testid="notes"
              disabled={isGiftcardDisabled}
              value={state.giftCardNotes}
              rows="3"
              name="giftCardNotes"
              placeholder={t('regularGiftcard.fields.notes', {
                context: 'placeholder',
              })}
              onChange={handleChange}
            />
          </InputGroup>
        </Form.Group>
      </Form>
    </PluginComponent>
  );
};

export default GiftCard;
